/**
 * The single icon map. See DESIGN.md §3.7.
 *
 * Components import the semantic name (`StatusPending`), never the Tabler name
 * (`IconClockHour4`). Changing which glyph represents "pending" is then one edit here
 * instead of forty across the app.
 *
 * Two rules this file exists to enforce:
 *   1. No `lucide-react`. shadcn/ui pastes lucide imports into every component you add —
 *      swap them for a name from this file. Biome fails the build otherwise.
 *   2. Named imports only. A barrel import of @tabler/icons-react pulls ~5900 icons into
 *      the bundle and tree-shaking will not always save you.
 *
 * Emails are the exception: Gmail strips inline SVG, so packages/mail uses hosted PNGs
 * or no icons at all. Nothing in this file belongs in a mail template.
 */

import type { Icon } from "@tabler/icons-react";
import {
  IconAlertCircle,
  IconAlertTriangle,
  IconArmchair,
  IconBan,
  IconBeach,
  IconBell,
  IconBellOff,
  IconBellRinging,
  IconBrandWhatsapp,
  IconBuildingCommunity,
  IconBuildingStore,
  IconCalendar,
  IconCalendarClock,
  IconCalendarPlus,
  IconCalendarX,
  IconCamera,
  IconCar,
  IconCarGarage,
  IconChartBar,
  IconCheck,
  IconChecks,
  IconChevronDown,
  IconChevronLeft,
  IconChevronRight,
  IconChevronUp,
  IconCircleCheck,
  IconCircleFilled,
  IconClipboardCheck,
  IconClipboardList,
  IconClockHour4,
  IconCoffee,
  IconCoins,
  IconCopy,
  IconCurrencyKroneSwedish,
  IconDots,
  IconDownload,
  IconFileAnalytics,
  IconFilter,
  IconFirstAidKit,
  IconGripVertical,
  IconHeartbeat,
  IconHistory,
  IconInfoCircle,
  IconKey,
  IconLanguage,
  IconLayoutDashboard,
  IconLayoutSidebar,
  IconLoader2,
  IconLock,
  IconLogout,
  IconMail,
  IconMailX,
  IconMapPin,
  IconMessage,
  IconMinus,
  IconMoon,
  IconMovie,
  IconNotes,
  IconPackage,
  IconPalette,
  IconPlayerPlay,
  IconPlugConnected,
  IconPlus,
  IconRefresh,
  IconRoute,
  IconSailboat,
  IconSchool,
  IconSearch,
  IconSelector,
  IconSettings,
  IconShieldCheck,
  IconShoppingBag,
  IconShoppingCart,
  IconSun,
  IconSunMoon,
  IconTag,
  IconTrash,
  IconTrendingDown,
  IconTrendingUp,
  IconTruck,
  IconUnlink,
  IconUser,
  IconUserOff,
  IconUserShield,
  IconUsers,
  IconVolume,
  IconVolumeOff,
  IconWind,
  IconX,
} from "@tabler/icons-react";

export type { Icon } from "@tabler/icons-react";

/* ── Status ──────────────────────────────────────────────────────────────────
 * Paired with the fill and left-border treatments in DESIGN.md §3.2. Status is
 * never communicated by colour alone.
 */

export const StatusPending = IconClockHour4;
export const StatusConfirmed = IconCheck;
export const StatusInProgress = IconPlayerPlay;
export const StatusCompleted = IconChecks;
export const StatusCancelled = IconX;
/** IconUserOff, not a warning triangle: "the person didn't come", not "something broke". */
export const StatusNoShow = IconUserOff;
/** An event synced in from Google. Visible, never editable. */
export const StatusExternal = IconLock;

/* ── Navigation ───────────────────────────────────────────────────────────── */

export const NavCalendar = IconCalendar;
export const NavBookings = IconClipboardList;
export const NavCustomers = IconUsers;
export const NavReports = IconChartBar;
export const NavSettings = IconSettings;
/** HQ: the list of workshops across the network. */
export const NavWorkshops = IconBuildingStore;
export const NavOverview = IconLayoutDashboard;
export const NavShop = IconShoppingBag;
export const NavShopOrders = IconPackage;
export const NavShopProducts = IconTag;
/** The signed-in user's own language, in the account menu. */
export const NavLanguage = IconLanguage;
/** Light or dark, beside the language in the same menu. */
export const NavTheme = IconSunMoon;
export const ThemeLight = IconSun;
export const ThemeDark = IconMoon;

/* ── Settings sections ────────────────────────────────────────────────────────
 *
 * The sidebar collapses to a 48px icon rail, where a nav item with no icon is a blank
 * square. These are the seven settings screens, which had none.
 */

export const SettingsHours = IconClockHour4;
export const SettingsStaff = IconUsers;
export const SettingsServices = IconTag;
export const SettingsRules = IconClipboardCheck;
export const SettingsReminders = IconBell;
export const SettingsMobile = IconTruck;
/** The workshop's own details — address, contact, publication. Not the signed-in person. */
export const SettingsProfile = IconBuildingStore;
/** Google Calendar sync and the ICS feeds beside it (P11). */
export const SettingsCalendar = IconCalendar;
export const SettingsIntegrations = IconPlugConnected;
export const ChannelWhatsapp = IconBrandWhatsapp;
export const NetworkDemand = IconTrendingUp;
export const NetworkHealth = IconHeartbeat;
export const NetworkScheduledReports = IconFileAnalytics;
export const NetworkCosts = IconCoins;
/** The signed-in person's own bell — sound, desktop, which events. Not the customer's. */
export const SettingsNotifications = IconBell;

/* ── The staff bell ───────────────────────────────────────────────────────────
 *
 * `NavNotifications` is the resting bell and `NavNotificationsUnread` the ringing one.
 * Two glyphs rather than one plus a dot, because the badge carries a number and DESIGN.md
 * §3.2 does not let a count be the only thing that changed.
 */

export const NavNotifications = IconBell;
export const NavNotificationsUnread = IconBellRinging;
export const NotificationMuted = IconBellOff;
export const SoundOn = IconVolume;
export const SoundOff = IconVolumeOff;

/* ── Services (BUILD.md §0) ───────────────────────────────────────────────── */

export const ServiceCarInterior = IconCar;
export const ServiceBoatInterior = IconSailboat;
export const ServiceFurniture = IconArmchair;
export const ServicePublicSpace = IconBuildingCommunity;
export const ServiceOdourRemoval = IconWind;
export const ServiceAgeStop = IconShieldCheck;
export const ServicePersonalTouch = IconPalette;

/* ── Resources ────────────────────────────────────────────────────────────── */

export const ResourceTechnician = IconUser;
export const ResourceVan = IconTruck;
export const ResourceBay = IconCarGarage;

/* ── Blockers ─────────────────────────────────────────────────────────────── */

export const BlockerVacation = IconBeach;
export const BlockerSick = IconFirstAidKit;
export const BlockerTraining = IconSchool;
export const BlockerBreak = IconCoffee;

/* ── Actions ──────────────────────────────────────────────────────────────── */

export const ActionAdd = IconPlus;
/**
 * Dismiss a sheet, a toast, a filter chip.
 *
 * The same glyph as `StatusCancelled`, and a separate name on purpose: they mean different
 * things, and the day "cancelled" gets its own mark this file is the one edit rather than a
 * hunt for which `IconX` meant which. That is what the map is for.
 */
export const ActionClose = IconX;
export const ActionFilter = IconFilter;
export const ActionPrevious = IconChevronLeft;
export const ActionNext = IconChevronRight;
export const ActionDrag = IconGripVertical;
export const ActionPhoto = IconCamera;
export const ActionNotes = IconNotes;
export const ActionReminders = IconBell;
export const ActionEmail = IconMail;
export const ActionSms = IconMessage;
export const ActionExport = IconDownload;
export const ActionAuditLog = IconHistory;
export const ActionLocation = IconMapPin;
export const ActionRoute = IconRoute;
export const ActionSync = IconRefresh;
export const ActionCopy = IconCopy;
export const ActionUnlink = IconUnlink;
export const ActionImpersonate = IconUserShield;
export const ActionSignOut = IconLogout;
export const ActionPassword = IconKey;
export const ActionSearch = IconSearch;
export const ActionVideo = IconMovie;
export const ActionCart = IconShoppingCart;
export const ActionDecrease = IconMinus;
export const ActionRemove = IconTrash;

/* ── shadcn/ui primitives ─────────────────────────────────────────────────────
 *
 * Every glyph a pasted shadcn component asks `lucide-react` for, named the way this file
 * names things. Swapping the import at paste time is the rule; these are what it swaps to.
 */

export const StatusLoading = IconLoader2;
export const ActionExpand = IconChevronDown;
export const ActionCollapse = IconChevronUp;
export const ActionSelect = IconSelector;
export const ActionMore = IconDots;
export const ActionToggleSidebar = IconLayoutSidebar;
export const IndicatorCheck = IconCheck;
export const IndicatorDot = IconCircleFilled;

/* ── Metrics ──────────────────────────────────────────────────────────────── */

export const MetricUp = IconTrendingUp;
export const MetricDown = IconTrendingDown;
export const MetricRevenue = IconCurrencyKroneSwedish;

/* ── Feedback ─────────────────────────────────────────────────────────────── */

export const FeedbackWarning = IconAlertTriangle;
export const FeedbackInfo = IconInfoCircle;
export const FeedbackSuccess = IconCircleCheck;
export const FeedbackError = IconAlertCircle;

/* ── Keyed lookups ────────────────────────────────────────────────────────── */

/**
 * For rendering a list of appointments without a switch statement at every call site.
 * `external` is not an appointment status — it is how a Google-sourced blocker renders.
 */
export const appointmentStatusIcons = {
  pending: StatusPending,
  confirmed: StatusConfirmed,
  in_progress: StatusInProgress,
  completed: StatusCompleted,
  cancelled: StatusCancelled,
  no_show: StatusNoShow,
  external: StatusExternal,
} as const satisfies Record<string, Icon>;

export const serviceIcons = {
  bilinredning: ServiceCarInterior,
  batinredning: ServiceBoatInterior,
  mobler: ServiceFurniture,
  offentlig_miljo: ServicePublicSpace,
  luktsanering: ServiceOdourRemoval,
  age_stop: ServiceAgeStop,
  personlig_pragel: ServicePersonalTouch,
} as const satisfies Record<string, Icon>;

export const resourceIcons = {
  staff: ResourceTechnician,
  van: ResourceVan,
  bay: ResourceBay,
} as const satisfies Record<string, Icon>;

/** One glyph per staff notification kind, so the bell renders a list without a switch. */
export const staffNotificationIcons = {
  booking_requested: StatusPending,
  booking_created: IconCalendarPlus,
  booking_rescheduled: IconCalendarClock,
  booking_cancelled: IconCalendarX,
  email_failed: IconMailX,
  shop_order_approved: StatusConfirmed,
  shop_order_rejected: IconBan,
  shop_order_shipped: IconTruck,
  shop_order_cancelled: StatusCancelled,
} as const satisfies Record<string, Icon>;

export const hqNotificationIcons = {
  shop_order_placed: IconPackage,
  shop_order_cancelled: StatusCancelled,
  shop_order_delivered: IconChecks,
} as const satisfies Record<string, Icon>;

export const shopOrderStatusIcons = {
  submitted: StatusPending,
  approved: StatusConfirmed,
  partially_shipped: IconTruck,
  delivered: IconChecks,
  rejected: IconBan,
  shipped: IconTruck,
  cancelled: StatusCancelled,
} as const satisfies Record<string, Icon>;

export const blockerIcons = {
  vacation: BlockerVacation,
  sick: BlockerSick,
  training: BlockerTraining,
  break: BlockerBreak,
} as const satisfies Record<string, Icon>;

/* ── Sizing ───────────────────────────────────────────────────────────────── */

/**
 * Tabler is drawn at 24px with a 2px stroke. Downscaled, that stroke reads heavier than
 * it should, so smaller sizes thin it out. DESIGN.md §3.7.
 *
 * Usage: `<StatusPending {...iconSize.inline} aria-hidden="true" />`
 */
export const iconSize = {
  /** Calendar event badges, table cells, inline with body-sm. */
  inline: { size: 16, stroke: 1.75 },
  /** Buttons, nav, inline with body. The default. */
  control: { size: 20, stroke: 2 },
  /** Empty states, step headers, feature cards. */
  feature: { size: 28, stroke: 2 },
  /** Illustration scale — empty states, booking confirmation. */
  illustration: { size: 48, stroke: 1.5 },
} as const;

export type IconSizeToken = keyof typeof iconSize;
